/**
 * OBV vs its moving-average "age" — signed bars since cumulative On-Balance
 * Volume last crossed its MA.  +N = OBV has been at/above its MA for N bars,
 * −N = below for N bars (0 until the first cross).
 *
 * Same signed bars-since-cross convention as the RSMA age (`./rs.ts`), so
 * "OBV Daily Age" reads exactly like "RS Daily/Weekly Age". The MA is
 * configurable (length + EMA/SMA/RMA/WMA); default EMA/13, so the scanner's
 * precomputed OBV Daily Age/Trend agrees with the chart's "OBV + MA" panel.
 */
import type { OHLCV, SharedIndicatorDef, SharedParam } from "../types";
import { familyGroup } from "../indicatorGroups";
import { TF_DW, TF_PARAM } from "./timeframe";
import { maFull, signedMaCrossAge, MA_TYPE_CHOICES } from "./ta";
import { bucketKey, htIndexPerDay } from "./weekly";

/** OBV's MA convention = EMA(13): SweeterTrade's "OBV with EMA", which its own
 *  catalog describes as "On Balance Volume and its 13-bar EMA" (the owner,
 *  2026-11-02, for the chart and the scanner alike). It was EMA(10) from
 *  2026-10-22 (the Jeffrey Scott deck of 2026-10-19) and EMA(14) from 2026-07-13,
 *  to match RS; RS stays EMA(14). Single source: the raw-math defaults, the
 *  configurable indicator params, the scanner-panel column
 *  (`lib/scanner/indicators.ts`), the ETF boards and the chart's `mda-obv-sma`
 *  default (customIndexEtf.ts) all read these, so scanner == chart. */
export const OBV_MA_LEN = 13;
export const OBV_MA_TYPE = 0; // 0 = EMA (see MA_TYPE_CHOICES)

/** EMA lengths that were this default before (`OBV_MA_LEN`): a chart saved with
 *  one of them reads today's, so a saved panel and the scanner's OBV Trend/Age
 *  agree. A length set by hand to anything else is used as set. */
export const OBV_RETIRED_EMA_LENS: ReadonlyArray<number> = [10, 14];

/** OBV "MA Age" params — EMA/13 default, so OBV Trend/Age mirrors the chart's
 *  OBV panel. */
const OBV_MA_AGE_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "length", label: "MA Length", kind: "number", default: OBV_MA_LEN, min: 2, max: 200, step: 1 },
  { id: "maType", label: "MA Type", kind: "select", default: OBV_MA_TYPE, choices: MA_TYPE_CHOICES },
];

/** Classic cumulative OBV from closes + volumes (index-aligned to closes). */
function obvCumulative(closes: number[], volumes: number[]): number[] {
  const n = closes.length;
  const out = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    const v = volumes[i] ?? 0;
    out[i] =
      out[i - 1] +
      (closes[i]! > closes[i - 1]! ? v : closes[i]! < closes[i - 1]! ? -v : 0);
  }
  return out;
}

/** Signed bars-since cumulative OBV crossed its MA. Optional MA length + type
 *  (default EMA/13, matching the chart's "OBV + MA" panel).
 *  +N above N bars, −N below. */
export function obvMaAge(data: OHLCV, length = OBV_MA_LEN, maType = OBV_MA_TYPE): (number | null)[] {
  const { closes, volumes } = data;
  const n = closes.length;
  if (n === 0 || !volumes || volumes.length !== n) return new Array(n).fill(null);
  const obv = obvCumulative(closes, volumes);
  return signedMaCrossAge(obv, maFull(obv, length, maType));
}

/** OBV trend state (+1 above its MA / -1 below / 0) from the signed age. */
const trendOf = (age: (number | null)[]): (number | null)[] =>
  age.map((v) => (v == null ? null : v > 0 ? 1 : v < 0 ? -1 : 0));
const OBV_TREND_LABELS = { pos: "Bull", neg: "Bear", zero: "—" } as const;

// One def per concept + a Timeframe select (default Daily). Daily/Weekly run the
// same OBV-vs-MA-age math (`obvMaAge` / `obvMaWeeklyAge`, hoisted below). The ids
// keep their historical "-daily" spelling, reused as canonical; only the weekly
// ids alias. Weekly stays instant via the tf-resolver in dynamicIndicatorColumns.

/** Configurable "OBV Age" — UNSIGNED bars/weeks since cumulative OBV last crossed
 *  its MA (a count, like Bongo Age); direction is OBV Trend. */
export const OBV_D_AGE: SharedIndicatorDef = {
  id: "mpp-obv-daily-age",
  label: "Afiat - OBV + MA Age",
  group: familyGroup("rs"),
  format: "age",
  noLiveOverlay: true,
  params: [TF_DW, ...OBV_MA_AGE_PARAMS],
  computeSeries: (data, p) =>
    (p[TF_PARAM] === 1 ? obvMaWeeklyAge(data, p.length, p.maType) : obvMaAge(data, p.length, p.maType)).map(
      (v) => (v == null ? null : Math.abs(v)),
    ),
};

/** OBV Trend — cumulative OBV above (Bull) / below (Bear) its MA. */
export const OBV_D_TREND: SharedIndicatorDef = {
  id: "mpp-obv-daily-trend",
  label: "Afiat - OBV + MA",
  group: familyGroup("rs"),
  kind: "state",
  format: "state",
  stateLabels: OBV_TREND_LABELS,
  noLiveOverlay: true,
  params: [TF_DW, ...OBV_MA_AGE_PARAMS],
  computeSeries: (data, p) =>
    trendOf(p[TF_PARAM] === 1 ? obvMaWeeklyAge(data, p.length, p.maType) : obvMaAge(data, p.length, p.maType)),
};

/** OBV-vs-MA age on WEEKLY bars: resample daily → weekly (last close + summed
 *  volume per ISO week, incl. the in-progress week), run the OBV-MA-age on that,
 *  then project onto daily indices so the latest row reads the current week.
 *  Mirrors the weekly RSMA age (`rsmaWeekly`). */
export function obvMaWeeklyAge(data: OHLCV, length = OBV_MA_LEN, maType = OBV_MA_TYPE): (number | null)[] {
  const { closes, volumes, times } = data;
  const n = closes.length;
  const empty = (): (number | null)[] => new Array(n).fill(null);
  if (n === 0 || !volumes || volumes.length !== n || !times || times.length !== n) return empty();
  const wTimes: number[] = [], wCloses: number[] = [], wVols: number[] = [];
  let key = bucketKey(times[0]!, "week");
  let bc = closes[0]!, bv = volumes[0]! || 0, bt = times[0]!;
  for (let i = 1; i < n; i++) {
    const k = bucketKey(times[i]!, "week");
    if (k === key) { bc = closes[i]!; bv += volumes[i]! || 0; bt = times[i]!; }
    else { wTimes.push(bt); wCloses.push(bc); wVols.push(bv); key = k; bc = closes[i]!; bv = volumes[i]! || 0; bt = times[i]!; }
  }
  wTimes.push(bt); wCloses.push(bc); wVols.push(bv);
  const m = wCloses.length;
  if (m < 16) return empty();
  const wAge = obvMaAge({ closes: wCloses, volumes: wVols } as OHLCV, length, maType);
  const idxPerDay = htIndexPerDay(times, wTimes);
  return times.map((_, i) => { const k = idxPerDay[i]!; return k >= 0 && k < m ? wAge[k]! : null; });
}
